<template>
  <div
    class="text-media"
    :class="{ 'sticky-mode': !isMobile && hasStickyMode }"
    :style="{
      '--text-position': textPosition,
    }"
  >
    <article class="texto">
      <header
        v-if="title || subtitle"
        class="base-title-fontsize"
      >
        <slot name="title">
          <h2
            v-if="title"
            class="tit tit-g"
          >
            {{ title }}
          </h2>
        </slot>
        <slot
          v-if="subtitle"
          name="subtitle"
        >
          <h3 class="sub">
            {{ subtitle }}
          </h3>
        </slot>
      </header>

      <div
        class="txt"
        :class="{ 'txt--no-buttons': !filteredButtons.length }"
      >
        <div
          v-if="!isMobile"
          class="custom-scrollbar inner-text"
        >
          <div v-html="text"></div>
        </div>
        <slot
          v-else
          name="inner-text"
        >
          <div
            class="inner-text"
            v-html="text"
          ></div>
        </slot>
      </div>

      <slot name="content"></slot>

      <slot
        v-if="isMobile"
        name="read-more"
      ></slot>

      <footer v-if="filteredButtons.length">
        <slot name="footer"></slot>
        <ul class="links">
          <li
            v-for="(button, index) in filteredButtons"
            :key="index"
            class="base-content-fontsize"
          >
            <EditorLink
              :model="getButtonModelProp(button)"
              class="cta"
              :class="{
                vazado: !button.buttonFeatured,
              }"
            >
              {{ button.buttonText }}
            </EditorLink>
          </li>
        </ul>
      </footer>
    </article>

    <div
      ref="swiper"
      :key="swiperMilliseconds"
      class="swiper"
    >
      <!-- Additional required wrapper -->
      <div class="swiper-wrapper">
        <div
          v-for="(slide, idx) in slides"
          :key="slide.image || idx"
          class="swiper-slide"
        >
          <iframe
            v-if="slide.type === 'video'"
            :src="
              youtubeEmbed({
                url: slide.videoSrc,
                controls: !slide.hideYoutubeControls,
              })
            "
          ></iframe>
          <LImage
            v-else
            class="absolute-full"
            :src="
              isMobile && slide.differentMobileBackgroundImage
                ? slide.imageMobile
                : slide.image
            "
            :alt="
              isMobile &&
              slide.differentMobileBackgroundImage &&
              slide.mobileAltText
                ? slide.mobileAltText
                : slide.altText
            "
          />
        </div>
      </div>

      <!-- If we need navigation buttons -->
      <div
        v-show="hasMultipleSlides"
        :class="`swiper-button-prev btn-prev-${instanceId}`"
      ></div>
      <div
        v-show="hasMultipleSlides"
        :class="`swiper-button-next btn-next-${instanceId}`"
      ></div>

      <slot name="swiper"></slot>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import Swiper from 'swiper';
import { Autoplay, Navigation } from 'swiper/modules';

import showButton from '@/mixins/showButton';
import EditorLink from '@/modules/EditorLink.vue';
import { youtubeEmbed } from '@/helpers/youtube';

export default {
  name: 'TextAndMedia',
  components: {
    EditorLink,
  },
  mixins: [showButton],
  inheritAttrs: false,
  props: {
    instanceId: {
      type: String,
      default: '',
    },
    title: {
      type: String,
      default: '',
    },
    subtitle: {
      type: String,
      default: '',
    },
    text: {
      type: String,
      default: '',
    },
    textPosition: {
      type: String,
      default: 'row',
    },
    hasStickyMode: {
      type: Boolean,
      default: true,
    },
    swiperAutoplay: {
      type: Boolean,
      default: false,
    },
    swiperAutoplaySpeed: {
      type: [String, Number],
      default: 10,
    },
    buttons: {
      type: Array,
      default() {
        return [];
      },
    },
    slides: {
      type: Array,
      default() {
        return [];
      },
    },
    getButtonModelProp: {
      type: Function,
      default: ({ buttonUrl } = {}) => buttonUrl,
    },
    paddingTop: {
      type: Number,
      default: 0,
    },
    paddingBottom: {
      type: Number,
      default: 0,
    },
    paddingTopMobile: {
      type: Number,
      default: 56,
    },
    paddingBottomMobile: {
      type: Number,
      default: 56,
    },
  },
  data() {
    return {
      mobileMaxWidth: 1020,
      swiper: null,
      isMobile: false,
    };
  },
  computed: {
    hasMultipleSlides() {
      return this.slides.length > 1;
    },
    swiperMilliseconds() {
      if (
        this.swiperAutoplaySpeed &&
        !Number.isNaN(Number(this.swiperAutoplaySpeed))
      ) {
        return Number(this.swiperAutoplaySpeed) * 1000;
      }
      return 0;
    },
    swiperAutoplaySettings() {
      if (
        this.swiperAutoplay &&
        this.swiperAutoplaySpeed &&
        !Number.isNaN(Number(this.swiperAutoplaySpeed))
      ) {
        return {
          delay: this.swiperMilliseconds,
        };
      }
      return false;
    },
    editing() {
      return this.$router.history.current.name === 'editor';
    },
    filteredButtons() {
      return this.buttons.filter((button) =>
        this.showButton(button.buttonText),
      );
    },
    ...mapState(['currentEditableItem', 'activeInstance']),
  },
  watch: {
    slides() {
      if (this.swiper) {
        this.swiperDestroy();
      }

      if (this.hasMultipleSlides) {
        this.$nextTick(() => {
          this.swiperInit();
        });
      }
    },
    currentEditableItem() {
      this.slideToActivatedItem();
    },
  },
  mounted() {
    this.onResize();
    window.addEventListener('resize', this.onResize);

    if (this.hasMultipleSlides) {
      this.swiperInit();
    }
  },
  methods: {
    slideToActivatedItem() {
      if (
        this.activeInstance === this.instanceId &&
        this.currentEditableItem != null
      ) {
        if (this.currentEditableItem.includes('slides')) {
          const itemPathIndex = Number(
            this.currentEditableItem.split('.').pop(),
          );
          if (this.swiper) {
            this.swiper.slideTo(itemPathIndex);
          }
        }
      }
    },
    swiperInit() {
      // O lazyload não funciona nos clones quando usamos a config de loop, deixando slides em branco.
      this.swiper = new Swiper(this.$refs.swiper, {
        modules: [Navigation, Autoplay],
        autoplay: this.editing ? false : this.swiperAutoplaySettings,
        rewind: true,
        navigation: {
          nextEl: `.btn-next-${this.instanceId}`,
          prevEl: `.btn-prev-${this.instanceId}`,
        },
        observer: true,
      });
      this.slideToActivatedItem();
    },
    swiperDestroy() {
      this.swiper.destroy();
      this.swiper = null;
    },
    onResize() {
      const isMobile = window.innerWidth < this.mobileMaxWidth;

      // If "device" changed...
      if (isMobile !== this.isMobile) {
        this.isMobile = isMobile;
      }
    },
    youtubeEmbed,
  },
  metadata: {
    tabs: [
      {
        name: 'content',
        groups: [
          {
            name: 'text',
            fields: [
              {
                name: 'text',
                type: 'Html',
                label: 'Texto de apoio',
                wrapWithParagraph: false,
              },
              {
                name: 'textPosition',
                type: 'VueSelect',
                label: 'Posição do texto',
                options: [
                  {
                    label: 'Esquerda',
                    value: 'row',
                  },
                  {
                    label: 'Direita',
                    value: 'row-reverse',
                  },
                ],
              },
            ],
          },
          {
            name: 'gallery',
            label: 'Galeria',
            fields: [
              {
                name: 'slides',
                type: 'List',
                itemLabel: 'Mídia {index}', // any {index} will be replaced by item's index (1-based)
                addItemLabel: 'Adicionar mídia',
                fields: [
                  {
                    name: 'type',
                    type: 'VueSelect',
                    label: 'Tipo de mídia',
                    default: 'image',
                    options: [
                      {
                        label: 'Imagem',
                        value: 'image',
                      },
                      {
                        label: 'Vídeo',
                        value: 'video',
                      },
                    ],
                  },
                  {
                    name: 'videoSrc',
                    type: 'Url',
                    label: 'URL do vídeo',
                    availableTypes: ['externalLink'],
                    allowNewTab: false,
                    isEnabled() {
                      return this.type === 'video';
                    },
                  },
                  {
                    name: 'hideYoutubeControls',
                    type: 'Checkbox',
                    default: false,
                    label: 'Ocultar controles de vídeo',
                    isEnabled() {
                      return this.type === 'video';
                    },
                  },
                  {
                    name: 'image',
                    type: 'ImageFile',
                    maxWidth: 960,
                    label: 'Imagem',
                    helpText:
                      'Dimensões recomendadas: 960x650.<br>As dimensões podem variar dependendo do conteúdo e espaçamentos do módulo.',
                    isEnabled() {
                      return this.type !== 'video';
                    },
                  },
                  {
                    name: 'altText',
                    type: 'ShortText',
                    label: 'Texto alternativo',
                    isEnabled() {
                      return this.type !== 'video';
                    },
                  },
                  {
                    name: 'differentMobileBackgroundImage',
                    type: 'Checkbox',
                    label: 'Usar uma imagem diferente para mobile',
                    isEnabled() {
                      return this.type !== 'video';
                    },
                  },
                  {
                    name: 'imageMobile',
                    type: 'ImageFile',
                    maxWidth: 960,
                    label: 'Imagem mobile',
                    helpText: 'Dimensões recomendadas: 720x512',
                    isEnabled() {
                      return (
                        this.type !== 'video' &&
                        this.differentMobileBackgroundImage
                      );
                    },
                  },
                  {
                    name: 'altTextMobile',
                    type: 'ShortText',
                    label: 'Texto alternativo mobile',
                    isEnabled() {
                      return (
                        this.type !== 'video' &&
                        this.differentMobileBackgroundImage
                      );
                    },
                  },
                ],
              },
            ],
          },
          {
            name: 'buttons',
            label: 'Botões',
            fields: [
              {
                type: 'List',
                name: 'buttons',
                maxItems: 2,
                itemLabel: 'Botão {index}', // any {index} will be replaced by item's index (1-based)
                addItemLabel: 'Adicionar botão',
                fields: [
                  {
                    name: 'buttonText',
                    type: 'ShortText',
                    label: 'Texto do botão',
                    maxlength: 15,
                  },
                  {
                    name: 'buttonUrl',
                    type: 'Url',
                  },
                  {
                    name: 'buttonFeatured',
                    type: 'Checkbox',
                    label: 'Destacar botão',
                  },
                ],
              },
            ],
          },
          {
            name: 'config',
            label: 'Configuração',
            fields: [
              {
                name: 'swiperAutoplay',
                type: 'Checkbox',
                label: 'Habilitar transição automática das imagens (autoplay)',
              },
              {
                name: 'swiperAutoplaySpeed',
                type: 'Range',
                label: 'Velocidade da transição das imagens',
                min: 1,
                max: 30,
                step: 1,
                format: {
                  suffix: 's',
                },
                isEnabled({ swiperAutoplay }) {
                  return swiperAutoplay === true;
                },
              },
            ],
          },
        ],
      },
    ],
  },
};
</script>

<style scoped lang="scss">
@import '@/styles/libs/swiper';

.text-media {
  display: flex;
  position: relative;
  z-index: 0;
  flex-direction: column-reverse;
  padding: 0;
  padding-top: var(--padding-top-mobile);
  padding-bottom: var(--padding-bottom-mobile);

  @include media-breakpoint-up(lg) {
    padding: 0;
    height: 480px;
    flex-direction: var(--text-position);
    padding-top: var(--padding-top);
    padding-bottom: var(--padding-bottom);
  }

  .texto {
    padding: 50px 6.25% 0;
    display: flex;
    flex-direction: column;

    @include media-breakpoint-up(lg) {
      padding: 4.75% 6.25%;
      width: 50%;
      justify-content: center;
      box-shadow: none;
    }

    .txt {
      margin: 0;
      line-height: 1.7;
      overflow: hidden;

      .inner-text {
        font-weight: $lb-medium;
        font-size: 1em;

        @include media-breakpoint-up(lg) {
          min-height: (1.5 * 4 * 15px);
        }

        &.custom-scrollbar {
          height: 100%;
          margin: 0;
          padding-right: 10px;
        }

        &::v-deep {
          strong {
            font-weight: $lb-bold !important;
          }

          a,
          a:visited {
            color: inherit;
            text-decoration: underline;
          }
        }
      }
    }

    header,
    footer {
      flex-grow: 0;
    }

    footer {
      @include media-breakpoint-up(lg) {
        margin-top: 12px;
        display: flex;
        flex-wrap: wrap;
        align-items: flex-end;
      }

      ul {
        list-style: none;

        @include media-breakpoint-up(lg) {
          flex: 1 1 auto;
          display: flex;
          flex-wrap: wrap;
          margin: -20px -15px 0;
        }

        li {
          .cta {
            margin: 16px 0 0;
            padding: 0.93em 2.81em;

            @include media-breakpoint-up(lg) {
              margin: 20px 15px 0;
              width: auto;
            }

            &.vazado {
              background-color: transparent;
            }
          }
        }
      }
    }
  }

  .swiper {
    width: 100%;

    @include media-breakpoint-up(lg) {
      width: 50%;
    }

    .swiper-slide {
      position: relative;

      &::before {
        content: '';
        padding-bottom: 68%;
        display: block;
      }

      iframe {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        border: 0;
      }
    }

    .swiper-button-prev,
    .swiper-button-next {
      width: 40px;

      @include media-breakpoint-up(sm) {
        width: 60px;
      }

      &::after {
        background-color: rgba($lb-black, 0.333);
        color: $lb-dark-gray;
        border-radius: 50px;
        width: 30px;
        height: 30px;
        font-size: 10px;
        display: flex;
        justify-content: center;
        align-items: center;

        @include media-breakpoint-up(sm) {
          width: 50px;
          height: 50px;
          font-size: 30px;
        }
      }
    }
  }

  &.sticky-mode {
    height: auto;
    min-height: 480px;

    @include media-breakpoint-up(lg) {
      height: auto;
    }

    .swiper {
      max-height: 100vh;
      position: sticky;
      top: 0;
    }
  }
}
</style>
